import type { ItemIndex } from '../../doc/ItemIndex';
import { rotatedBounds, type Rect } from '../../doc/geometry';

/** Vertical (x) and horizontal (y) lines other items offer as snap targets, in world units. */
export interface SnapCandidates {
  xs: number[];
  ys: number[];
}

export interface SnapResult {
  dx: number;
  dy: number;
  /** Matched guide positions for drawing, or null when no snap happened on that axis. */
  gx: number | null;
  gy: number | null;
}

/** Edges and centres of every unlocked item intersecting `area`, except the excluded ones. */
export function collectCandidates(index: ItemIndex, area: Rect, exclude: ReadonlySet<string>): SnapCandidates {
  const xs = new Set<number>();
  const ys = new Set<number>();
  for (const item of index.query(area)) {
    if (exclude.has(item.id)) continue;
    const b = rotatedBounds(item);
    xs.add(b.x);
    xs.add(b.x + b.w / 2);
    xs.add(b.x + b.w);
    ys.add(b.y);
    ys.add(b.y + b.h / 2);
    ys.add(b.y + b.h);
  }
  return { xs: [...xs].sort((a, b) => a - b), ys: [...ys].sort((a, b) => a - b) };
}

function best(edges: number[], cands: number[], threshold: number): { delta: number; at: number } | null {
  let out: { delta: number; at: number } | null = null;
  for (const e of edges) {
    for (const c of cands) {
      const d = c - e;
      if (Math.abs(d) <= threshold && (!out || Math.abs(d) < Math.abs(out.delta))) out = { delta: d, at: c };
    }
  }
  return out;
}

/**
 * Snap a moving rect (its left/centre/right and top/centre/bottom) to candidate lines and,
 * optionally, its top-left to a grid. Returns the correction to add to the rect.
 */
export function snapRect(rect: Rect, cands: SnapCandidates, threshold: number, grid: number | null): SnapResult {
  const res: SnapResult = { dx: 0, dy: 0, gx: null, gy: null };
  const bx = best([rect.x, rect.x + rect.w / 2, rect.x + rect.w], cands.xs, threshold);
  const by = best([rect.y, rect.y + rect.h / 2, rect.y + rect.h], cands.ys, threshold);
  if (bx) {
    res.dx = bx.delta;
    res.gx = bx.at;
  } else if (grid) {
    const g = Math.round(rect.x / grid) * grid - rect.x;
    if (Math.abs(g) <= threshold) res.dx = g;
  }
  if (by) {
    res.dy = by.delta;
    res.gy = by.at;
  } else if (grid) {
    const g = Math.round(rect.y / grid) * grid - rect.y;
    if (Math.abs(g) <= threshold) res.dy = g;
  }
  return res;
}

/** Snap a single edge position (used while resizing). Returns the corrected position and guide. */
export function snapEdge(pos: number, cands: number[], threshold: number, grid: number | null): { pos: number; guide: number | null } {
  const b = best([pos], cands, threshold);
  if (b) return { pos: b.at, guide: b.at };
  if (grid) {
    const g = Math.round(pos / grid) * grid;
    if (Math.abs(g - pos) <= threshold) return { pos: g, guide: null };
  }
  return { pos, guide: null };
}

/** Snap threshold in world units for the given zoom (8 screen pixels). */
export const snapThreshold = (zoom: number): number => 8 / zoom;
